import type { Signal } from '@angular/core';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Observable } from 'rxjs';

import {
  ProfileInformationModel,
  SidebarGroup,
  UserPermissions,
} from '../../models';
import {
  LoadProfileData,
  LoadUserRoles,
  ResetProfileLoadAndErrorState,
  SetUserLastLoginTime,
  UpdateSubmitSettingsStateValues,
  UpdateSubmitSettingsStatus,
  UpdateSubmitSettingsValues,
  UpdateUserLastLoginTime,
} from '../actions';
import { ProfileSelectors } from '../selectors/profile.selectors';

@Injectable({
  providedIn: 'root',
})
export class ProfileStoreService {
  get profileInformation(): Signal<ProfileInformationModel> {
    return this.store.selectSignal(ProfileSelectors.profileInformation);
  }

  get communicationLanguage(): Signal<string> {
    return this.store.selectSignal(
      (state) => state.settings.information.communicationLanguage,
    );
  }

  get lastLoginTime(): Signal<string> {
    return this.store.selectSignal(ProfileSelectors.lastLoginTime);
  }

  get sidebarMenu(): Signal<SidebarGroup[]> {
    return this.store.selectSignal(ProfileSelectors.sidebarMenu);
  }

  get submitSettingsStatus(): Signal<boolean> {
    return this.store.selectSignal(ProfileSelectors.submitSettingsStatus);
  }

  get profileInformationAccessLevel(): Observable<UserPermissions> {
    return this.store.select(ProfileSelectors.profileInformationAccessLevel);
  }

  get userRoles(): Signal<string[]> {
    return this.store.selectSignal(ProfileSelectors.userRoles);
  }

  get profileDataError$(): Observable<string | null> {
    return this.store.select(
      (state) => state.settings.loadingErrors.profileData,
    );
  }

  get profileDataLoaded$(): Observable<boolean> {
    return this.store.select(
      (state) => state.settings.loadedStates.profileData,
    );
  }

  get isProfileDataLoading(): Signal<boolean> {
    return this.store.selectSignal(ProfileSelectors.profileDataLoading);
  }

  get isProfileDataLoaded(): Signal<boolean> {
    return this.store.selectSignal(ProfileSelectors.profileDataLoaded);
  }

  get hasProfileDataApiError(): Signal<boolean> {
    return this.store.selectSignal(ProfileSelectors.hasProfileDataApiError);
  }

  get oscContactId(): Signal<string> {
    return this.store.selectSignal(ProfileSelectors.oscContactId);
  }

  constructor(private store: Store) {}

  @Dispatch()
  loadProfileData = () => new LoadProfileData();

  @Dispatch()
  setUserLastLoginTime = (userLastLoginTime: string) =>
    new SetUserLastLoginTime(userLastLoginTime);

  @Dispatch()
  updateUserLastLoginTime = (userLastLoginTime: string) =>
    new UpdateUserLastLoginTime(userLastLoginTime);

  @Dispatch()
  updateSubmitSettingsStatus = (isValid: boolean) =>
    new UpdateSubmitSettingsStatus(isValid);

  @Dispatch()
  updateSubmitSettingsStateValues = (formValue: any) =>
    new UpdateSubmitSettingsStateValues(formValue);

  @Dispatch()
  updateSubmitSettingsValues = () => new UpdateSubmitSettingsValues();

  @Dispatch()
  loadUserRoles = () => new LoadUserRoles();

  @Dispatch()
  resetProfileLoadAndErrorState = () => new ResetProfileLoadAndErrorState();

  hasPermission(
    category: string,
    permission: keyof UserPermissions[string],
  ): Signal<boolean> {
    return this.store.selectSignal(
      ProfileSelectors.hasPermission(category, permission),
    );
  }
}
